import { EventConfig } from './interfaces'

type Callback<T extends keyof EventConfig> = (message: EventConfig[T]) => void

export type EmbedChannel = {
  send: <T extends keyof EventConfig>(type: T, event: EventConfig[T]) => void
  subscribe: <T extends keyof EventConfig>(type: T, callback: Callback<T>) => void
  unsubscribe: <T extends keyof EventConfig>(type: T, callback: Callback<T>) => void
  destroy: () => void
  updateOrigin: (ancestor: { origin: string }) => void
}

export const createEmbedChannel = ({ sendTo, origin }: { sendTo: Window | null; origin?: string }): EmbedChannel => {
  origin = origin && origin !== '*' ? new URL(origin).origin : origin

  const callbacks: { [T in keyof EventConfig]?: Array<Callback<T>> } = {}

  const listener = (message: MessageEvent) => {
    if (origin && origin !== '*' && message.origin !== origin) {
      console.warn(`Message from ${message.origin} ignored, expected ${origin}`)
      return
    }

    const { type, ...data } = message.data

    callbacks[type as keyof EventConfig]?.forEach((callback) => {
      callback(data)
    })
  }

  window.addEventListener('message', listener)

  const updateOrigin = (ancestor: { origin: string }) => {
    origin = ancestor.origin && ancestor.origin !== '*' ? new URL(ancestor.origin).origin : ancestor.origin
  }

  const send = <T extends keyof EventConfig>(type: T, event: EventConfig[T]) => {
    if ('type' in event) {
      // We can not change this due to legacy integrations - partner side expect this property to be present
      throw new Error('Type property is not allowed in event data')
    }
    sendTo?.postMessage({ ...event, type }, origin ?? '*')
  }

  const subscribe = <T extends keyof EventConfig>(type: T, callback: Callback<T>) => {
    callbacks[type] ??= []
    callbacks[type].push(callback)
  }

  const unsubscribe = <T extends keyof EventConfig>(type: T, callback: Callback<T>) => {
    callbacks[type] = callbacks[type]?.filter((cb) => cb !== callback) as (typeof callbacks)[T]
  }

  const destroy = () => {
    Object.keys(callbacks).forEach((type) => (callbacks[type as keyof EventConfig] = []))
    window.removeEventListener('message', listener)
  }

  return { send, subscribe, unsubscribe, destroy, updateOrigin }
}
